///
/// EarthBrowser
///
/// Created by Matt Giger on 8/22/2024
/// Copyright (c) 2024 Geodata Labs
///

import { logger } from "@/utils/LogDispatcher"

/**
 * Maximum age for cache entries in milliseconds (1 year)
 */
export const cCacheMaxAge = 31536000000

/**
 * One week in milliseconds
 */
export const cCacheAgeOneWeek = 604800000

/**
 * Timeout for the fetch operation in milliseconds (30 seconds)
 */
const cTimeoutMillis = 30000

/**
 * Fetch priority hint. "high" for data the user is looking at right now,
 * "low" for speculative preloads. Over shared HTTP/2 connections the browser
 * reorders queued requests accordingly; no-op where unsupported.
 */
export type FetchPriority = "high" | "low" | "auto"

/**
 * Options for FetchLoader
 */
export interface FetchLoaderOptions {
	/** Cache lifetime in milliseconds. Defaults to cCacheMaxAge (1 year) */
	lifetime?: number
	/** Authorization token to include in request headers */
	authToken?: string
	/** Function to get auth token dynamically (called on each request) */
	getAuthToken?: () => Promise<string | null>
	/** Fetch priority hint. Defaults to "auto" */
	priority?: FetchPriority
}

/**
 * Interface for fetching data from a URL.
 */
export interface FetchInterface {
	/** The URL to fetch from */
	url: string
	/** Loads data from the URL */
	load(): Promise<ArrayBuffer | undefined>
	/** Aborts the ongoing fetch operation */
	abort(): void
	/** Indicates whether the fetch has been aborted */
	aborted: boolean
}

/**
 * FetchLoader class implements the FetchInterface for loading data from a URL.
 */
export class FetchLoader implements FetchInterface {
	/** The URL to fetch from */
	protected _url: string
	/** Flag indicating if the fetch has been aborted */
	protected _aborted: boolean = false
	/** AbortController for cancelling the fetch operation */
	protected _abortController?: AbortController
	/** Duration of lifetime for the cached data */
	protected _lifetime: number
	/** Timeout for the fetch operation */
	protected timeout: number = cTimeoutMillis
	/** Static authorization token */
	protected _authToken?: string
	/** Dynamic auth token getter */
	protected _getAuthToken?: () => Promise<string | null>
	/** Fetch priority hint */
	protected _priority: FetchPriority = "auto"
	/** HTTP status of the last response (0 when no response was received) */
	protected _status: number = 0

	/**
	 * Constructs a new FetchLoader instance.
	 * @param url The URL to fetch from.
	 * @param options Optional configuration (lifetime, authToken, getAuthToken)
	 */
	constructor(url: string, options?: FetchLoaderOptions | number) {
		this._url = url

		// Handle legacy number parameter for backwards compatibility
		if (typeof options === 'number') {
			this._lifetime = options
		} else {
			this._lifetime = options?.lifetime ?? cCacheMaxAge
			this._authToken = options?.authToken
			this._getAuthToken = options?.getAuthToken
			this._priority = options?.priority ?? "auto"
		}
	}

	/**
	 * Getter for the URL
	 * @returns The URL to fetch from
	 */
	public get url(): string {
		return this._url
	}

	/**
	 * HTTP status of the last response, or 0 when the request never got one
	 * (network error, timeout, abort, missing auth token). Lets callers tell
	 * a permanent miss (404) from a transient failure worth retrying.
	 */
	public get status(): number {
		return this._status
	}

	/**
	 * Loads data from the URL
	 * @returns Promise resolving to ArrayBuffer of fetched data or undefined if fetch fails
	 */
	public async load(): Promise<ArrayBuffer | undefined> {
		const controller = new AbortController()
		this._abortController = controller
		this._status = 0
		const timeoutSignal = AbortSignal.timeout(this.timeout)

		// Get auth token (dynamic takes precedence over static)
		let authToken = this._authToken
		if (this._getAuthToken) {
			authToken = await this._getAuthToken() ?? undefined
			if (!authToken) {
				// The endpoint requires a token and we couldn't mint one
				// (transient failure or retry cooldown) - skip the request
				// instead of sending a guaranteed 401
				return undefined
			}
		}

		// Build headers
		const headers: HeadersInit = {}
		if (authToken) {
			headers.Authorization = `Bearer ${authToken}`
		}

		try {
			const response = await fetch(this.url, {
				mode: 'cors',
				signal: AbortSignal.any([controller.signal, timeoutSignal]),
				headers,
				priority: this._priority
			})
			this._abortController = undefined
			this._status = response.status
			if(!response.ok) {
				return undefined
			}
			const data = await response.arrayBuffer()
			return data
		} catch (error) {
			if(error instanceof Error) {
				logger.log(error.message)
			} else if (typeof error === 'string') {
				logger.log(error)
			} else {
				logger.log('FetchLoader:', error)
			}
		}
	}

	/**
	 * Loads data from the URL and parses it as JSON.
	 * @returns Promise resolving to the parsed JSON data or undefined if fetch fails
	 */
	public async loadJson<T>(): Promise<T | undefined> {
		const data = await this.load()
		if(data) {
			return JSON.parse(new TextDecoder().decode(data)) as T
		}
		return undefined
	}

	/**
	 * Aborts the current fetch operation if one is in progress
	 */
	public abort(): void {
		// Always mark aborted, even when no fetch is in flight - the load may
		// be past the network stage (decoding, cache read) and consumers check
		// this flag before uploading into textures
		this._aborted = true
		if(this._abortController) {
			this._abortController.abort("cancelled")
			this._abortController = undefined
		}
	}

	/**
	 * Getter for the aborted state
	 * @returns True if the fetch has been aborted, false otherwise
	 */
	public get aborted(): boolean {
		return this._aborted
	}
}
